import React, { useState } from "react";
import { CheckCircle2, Loader2, PenLine, ThumbsDown, Type } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SignaturePad } from "./SignaturePad";
import { cn } from "@/lib/utils";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SCRIPT_FONT = "'Segoe Script', 'Brush Script MT', 'Snell Roundhand', cursive";

async function post(url: string, body: unknown) {
  const r = await fetch(url, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify(body) });
  const data = await r.json().catch(() => null);
  if (!r.ok) throw new Error(String(data?.Message || data?.message || "Something went wrong. Please try again.").split("\n")[0]);
  return data;
}

/**
 * The customer's Accept / Decline buttons for an open proposal, with the signing and decline dialogs.
 * `consentText` is the statement shown before signing (with "[your name]", filled in as they type).
 */
export function PublicDecisionPanel({
  token,
  displayNumber,
  customer,
  recipientName,
  consentText,
  disabledReason,
  contactName,
  contactEmail,
  onDecided,
}: {
  token: string;
  displayNumber: string;
  customer: string;
  recipientName: string | null;
  consentText: string;
  /** Shown instead of the buttons (e.g. a signed-in rep previewing the link) */
  disabledReason?: string;
  /** Who the customer talks to: named in the card, and "Request changes" emails them */
  contactName?: string | null;
  contactEmail?: string | null;
  onDecided: () => void;
}) {
  const [mode, setMode] = useState<"accept" | "decline" | null>(null);
  const [name, setName] = useState(recipientName || "");
  const [title, setTitle] = useState("");
  const [email, setEmail] = useState("");
  const [sigType, setSigType] = useState<"TYPED" | "DRAWN">("TYPED");
  const [drawn, setDrawn] = useState<string | null>(null);
  const [agree, setAgree] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState("");

  const base = `/api/Public/Proposal/${encodeURIComponent(token)}`;
  const statement = consentText.replace("[your name]", name.trim() || "[your name]").replace("typed or drawn", sigType === "DRAWN" ? "drawn" : "typed");

  const open = (m: "accept" | "decline") => {
    setProblem("");
    setAgree(false);
    setMode(m);
  };

  const accept = async (e: React.FormEvent) => {
    e.preventDefault();
    setProblem("");
    if (name.trim().length < 2) return setProblem("Enter your full name.");
    if (email.trim() && !EMAIL_RE.test(email.trim())) return setProblem("The email address doesn't look right.");
    if (sigType === "DRAWN" && !drawn) return setProblem("Draw your signature in the box.");
    if (!agree) return setProblem("Tick the box to confirm you agree.");
    setBusy(true);
    try {
      await post(`${base}/accept`, {
        signer_name: name.trim(),
        signer_title: title.trim() || undefined,
        signer_email: email.trim() || undefined,
        signature_type: sigType,
        signature_image: sigType === "DRAWN" ? drawn : undefined,
        agree: true,
      });
      setMode(null);
      onDecided();
    } catch (err: any) {
      setProblem(err.message);
    } finally {
      setBusy(false);
    }
  };

  const decline = async (e: React.FormEvent) => {
    e.preventDefault();
    setProblem("");
    if (!reason.trim()) return setProblem("Please tell us why, so we can do better.");
    setBusy(true);
    try {
      await post(`${base}/decline`, { reason: reason.trim(), signer_name: name.trim() || undefined });
      setMode(null);
      onDecided();
    } catch (err: any) {
      setProblem(err.message);
    } finally {
      setBusy(false);
    }
  };

  const input = "h-10 w-full rounded-lg border border-border bg-white px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40";

  return (
    <>
      <section id="accept" aria-label="Accept this proposal" className="scroll-mt-20 rounded-xl border-[1.5px] border-primary bg-accent/40 px-5 py-5">
        <h2 className="text-lg font-semibold text-foreground">Accept this proposal</h2>
        <p className="mt-0.5 text-[13px] text-muted-foreground">
          {disabledReason || `Your acceptance is recorded with the time and sent to ${contactName || "us"}. Nothing is charged online.`}
        </p>
        {!disabledReason && (
          <div className="mt-4 flex flex-wrap gap-2.5">
            <button type="button" onClick={() => open("accept")} className="inline-flex h-11 items-center gap-1.5 rounded-[9px] bg-primary px-5 text-sm font-semibold text-white hover:bg-primary/90">
              <CheckCircle2 className="size-4" /> Accept proposal
            </button>
            {contactEmail && (
              <a
                href={`mailto:${contactEmail}?subject=${encodeURIComponent(`Changes to proposal ${displayNumber}`)}`}
                className="inline-flex h-11 items-center rounded-[9px] border border-border bg-white px-4 text-sm font-medium text-foreground hover:bg-muted"
              >
                Request changes
              </a>
            )}
            <button type="button" onClick={() => open("decline")} className="h-11 px-3 text-sm text-danger hover:underline">
              Decline
            </button>
          </div>
        )}
      </section>

      <Dialog open={mode === "accept"} onOpenChange={(o) => !o && !busy && setMode(null)}>
        <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto bg-white text-foreground">
          <DialogHeader>
            <DialogTitle>Accept proposal {displayNumber}</DialogTitle>
            <DialogDescription className="text-muted-foreground">Sign below to accept on behalf of {customer || "your organisation"}.</DialogDescription>
          </DialogHeader>
          <form onSubmit={accept} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="space-y-1 text-sm font-medium text-foreground">
                Full name *
                <input className={input} value={name} maxLength={120} onChange={(e) => setName(e.target.value)} autoComplete="name" />
              </label>
              <label className="space-y-1 text-sm font-medium text-foreground">
                Job title
                <input className={input} value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} autoComplete="organization-title" />
              </label>
            </div>
            <label className="block space-y-1 text-sm font-medium text-foreground">
              Email for your signed copy
              <input className={input} type="email" value={email} maxLength={200} onChange={(e) => setEmail(e.target.value)} autoComplete="email" placeholder="Optional" />
            </label>

            <div className="space-y-2">
              <div className="flex gap-1 rounded-lg bg-muted p-1 text-xs font-semibold">
                {(
                  [
                    ["TYPED", "Type", Type],
                    ["DRAWN", "Draw", PenLine],
                  ] as const
                ).map(([id, label, Icon]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setSigType(id)}
                    className={cn("flex-1 h-8 rounded-md inline-flex items-center justify-center gap-1.5", sigType === id ? "bg-white shadow-sm text-foreground" : "text-muted-foreground")}
                  >
                    <Icon className="size-3.5" /> {label} signature
                  </button>
                ))}
              </div>
              {sigType === "TYPED" ? (
                <div className="h-[100px] rounded-lg border-2 border-dashed border-border bg-white flex items-center justify-center px-4">
                  <span style={{ fontFamily: SCRIPT_FONT }} className="text-3xl text-primary truncate">
                    {name.trim() || <span className="text-base text-muted-foreground font-sans">Your name appears here</span>}
                  </span>
                </div>
              ) : (
                <SignaturePad onChange={setDrawn} height={140} />
              )}
            </div>

            <div className="rounded-lg bg-muted border p-3 text-xs text-foreground whitespace-pre-line max-h-40 overflow-y-auto">{statement}</div>
            <label className="flex items-start gap-2 text-sm text-foreground cursor-pointer">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="size-4 mt-0.5 accent-success" />
              I agree to the statement above, and to signing electronically.
            </label>
            {problem && <p className="text-sm text-danger">{problem}</p>}
            <DialogFooter>
              <button type="button" disabled={busy} onClick={() => setMode(null)} className="h-10 px-4 rounded-lg border border-border text-sm font-semibold text-foreground">
                Cancel
              </button>
              <button type="submit" disabled={busy} className="h-10 px-5 rounded-lg bg-success text-white text-sm font-semibold hover:bg-success/90 inline-flex items-center justify-center gap-1.5 disabled:opacity-70">
                {busy ? <Loader2 className="size-4 animate-spin" /> : <CheckCircle2 className="size-4" />} Accept and sign
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={mode === "decline"} onOpenChange={(o) => !o && !busy && setMode(null)}>
        <DialogContent className="sm:max-w-md bg-white text-foreground">
          <DialogHeader>
            <DialogTitle>Decline proposal {displayNumber}</DialogTitle>
            <DialogDescription className="text-muted-foreground">We'd appreciate knowing why. It helps us make a better offer.</DialogDescription>
          </DialogHeader>
          <form onSubmit={decline} className="space-y-3">
            <label className="block space-y-1 text-sm font-medium text-foreground">
              Your name
              <input className={input} value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="block space-y-1 text-sm font-medium text-foreground">
              Reason *
              <textarea
                className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                rows={4}
                maxLength={1000}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. over budget, went with another supplier, project postponed"
              />
            </label>
            {problem && <p className="text-sm text-danger">{problem}</p>}
            <DialogFooter>
              <button type="button" disabled={busy} onClick={() => setMode(null)} className="h-10 px-4 rounded-lg border border-border text-sm font-semibold text-foreground">
                Cancel
              </button>
              <button type="submit" disabled={busy} className="h-10 px-5 rounded-lg bg-sidebar text-white text-sm font-semibold hover:bg-sidebar/90 inline-flex items-center justify-center gap-1.5 disabled:opacity-70">
                {busy ? <Loader2 className="size-4 animate-spin" /> : <ThumbsDown className="size-4" />} Decline
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
